// 바닥 드롭·줍기(D2a): 몹·엘리트·보스 처치 → 고정 크기 드롭 풀(위치·종류·등급) → 영웅이 걸어서 줍거나 동행 펫이 반경 안을 자동으로 줍는다.
// 규칙(드롭 수·등급·개체 굴림)은 shared/gear.ts(결정적). 판마다 씨앗을 다시 정해(게임 씨앗 + 판 번호) 같은 입력이면 같은 드롭이다.
// 프레임 루프: 풀 배열을 재사용하고 거리 비교만 한다. 장비 개체(저장 데이터)와 장부 연산은 처치 순간 한 번 만든다(이벤트 단위, 줍기 틱 할당 없음).
// 화면 피드백은 월드 빛기둥(render/loot.ts)과 화면 가장자리 띠(ui/lootFeed.ts)뿐 — 플레이 영역 위 팝업은 없다.
import { Ev } from '../shared/events';
import { Scene, combatScene } from '../shared/types';
import { PETS, namedFor, namedUniqueDef } from '../shared/content/items';
import { DIFFICULTY_ILVL_ADD, DIFFICULTY_LOOT, stoneTierForIlvl } from '../shared/content/economy';
import { REGIONS_90D, stageById } from '../shared/content/world';
import { GRADE_INDEX, HERO_PICKUP_RADIUS, LOOT_ARM_TICKS, LOOT_MAP, LOOT_POOL, LOOT_SCATTER, SWEEP_ON_CLEAR, liveBossDrops, lootStageFor } from '../shared/content/loot';
import { isRegionId } from '../shared/levels';
import { SLOT_ORDER } from '../shared/gearLedger';
import { hashSeed, itemSlot, nextRand, rollGear, rollKillDrops, rollNamedItem, seedRng, type DropContext, type GearItem, type KillDrops, type SeedRng } from '../shared/gear';
import type { GearOp } from '../shared/gearLedger';
import { PICKED_RING, type LootView } from '../shared/gearView';
import { GEM_COLORS, GEM_DROP, gemCode, gemDropTier } from '../shared/content/consumables';
import { firstClearGearPlan, type FirstClearReward } from '../shared/content/stageRewards';
import type { AnyStageId, StageId } from '../shared/content/types';
import { rewardId } from './save';
import type { GameSim } from './game';

/** 드롭 종류. 보석(D2c)은 grade 칸에 색 번호(GEM_COLORS 순서)를 둔다 */
export const LootKind = { Gear: 0, Stone: 1, Gem: 2 } as const;
/** 줍은 방법(Ev.LootPickup c) */
export const PickupBy = { Hero: 0, Pet: 1, Sweep: 2, Overflow: 3 } as const;

export class LootState implements LootView {
  readonly capacity = LOOT_POOL;
  readonly active = new Uint8Array(LOOT_POOL);
  readonly x = new Float32Array(LOOT_POOL);
  readonly z = new Float32Array(LOOT_POOL);
  readonly kind = new Uint8Array(LOOT_POOL);
  readonly grade = new Uint8Array(LOOT_POOL);
  readonly age = new Int32Array(LOOT_POOL);
  drops = 0;
  pickups = 0;
  petPickups = 0;
  petRadius = 0;
  readonly item: (GearItem | null)[] = new Array<GearItem | null>(LOOT_POOL).fill(null);
  readonly aux = new Int32Array(LOOT_POOL);
  readonly tier = new Uint8Array(LOOT_POOL);
  readonly picked: (GearItem | null)[] = new Array<GearItem | null>(PICKED_RING).fill(null);
  /** 줍기 고리의 다음 칸 */
  pickedHead = 0;
}

export class LootController {
  readonly state = new LootState();
  /**
   * 슬롯별 장부 연산(줍기·강화석). 드롭 순간(처치 이벤트) 한 번 만들어 두고, 줍는 고정 스텝에서는 그대로 장부에 넘긴다 —
   * 줍기 틱에 연산 id 문자열·객체를 만들지 않는다(c015 프레임 루프 할당 규칙). 비면 null
   */
  private readonly ops: (GearOp | null)[] = new Array(LOOT_POOL).fill(null);
  /** 슬롯을 연 순서(가득 찰 때 가장 오래된 것을 바로 가방에 넣는다) */
  private readonly order = new Int32Array(LOOT_POOL);
  private serial = 0;
  private orderSeq = 0;
  private runNonce = '';
  /** 이 판의 고유값(판 시작마다 새로 — 아바타 드롭 시드·연산 id가 쓴다) */
  get runKey(): string { return this.runNonce; }
  private runIndex = 0;
  private rng: SeedRng = seedRng(1);
  private readonly rand = (): number => nextRand(this.rng);
  /**
   * 보석 드롭 전용 난수(D2c): 같은 판 씨앗에서 따로 만든 수열. 장비·강화석 굴림 수열(rand)을 건드리지 않아
   * 보석을 넣기 전과 같은 입력이면 같은 장비 드롭이다(loot.test 기대값·시뮬 정합 유지)
   */
  private gemRng: SeedRng = seedRng(2);
  private readonly gemRand = (): number => nextRand(this.gemRng);
  /** 이름 있는 유니크 드롭 전용 난수(BALANCE-1028 3절): 장비·보석 수열을 건드리지 않는다(loot.test 기대값 유지) */
  private namedRng: SeedRng = seedRng(3);
  private readonly namedRand = (): number => nextRand(this.namedRng);
  /** 이 판 보석 등급(0 = 이 지역·난이도는 보석 없음) */
  private gemTier = 0;
  private readonly drops: KillDrops = { equips: 0, bossEquips: 0, stones: 0 };
  /** 지금 판의 드롭 문맥(던전일 때만) */
  private ctx: DropContext | null = null;
  private stoneMult = 1;
  /** 이 판의 드롭 스테이지(QA·시험, 마을이면 '') */
  stageId = '';
  /** 이 판의 드롭 표(마을이면 null) */
  get table(): string | null { return this.ctx ? this.ctx.table : null; }
  /** 이 판 드롭 난이도(D3b: 하드 판 = 'hard' — 아이템 레벨 +10·강화석 ×2·보석 +1). 마을 null */
  get diff(): string | null { return this.ctx ? this.ctx.diff : null; }
  /** 동행 펫 id(펫 시스템 전 훅: 정하면 그 펫 반경으로 자동 줍기) */
  petId: string | null = null;

  constructor(private readonly g: GameSim, private readonly seed: number) {}

  /**
   * 동행 펫 줍기 훅(PRD 4.7). 펫 시스템(동행·진화·버프)은 아직 없어 이 훅만 있다: id를 주면 그 펫 데이터의 pickupRadius로 줍는다.
   * null이면 영웅만 줍는다. 판 도중에도 바꿀 수 있다.
   */
  setPet(id: string | null): void {
    const pet = id ? PETS.find((p) => p.id === id) : undefined;
    this.petId = pet ? pet.id : null;
    this.state.petRadius = pet ? pet.pickupRadius : 0;
  }

  /** 판 시작(loadMap): 풀을 비우고 드롭 문맥·씨앗을 정한다. 마을은 드롭이 없다 */
  begin(mapId: string): void {
    this.clear();
    const s = this.state;
    s.drops = 0; s.pickups = 0; s.petPickups = 0;
    const m = isRegionId(mapId) ? LOOT_MAP[mapId] : null;
    if (!m) { this.ctx = null; this.stageId = ''; return; }
    // 이 판의 드롭 스테이지(D3a): 실제로 들어온 스테이지 — 일반·중간보스(5)·지역보스(10) 표가 진행을 그대로 따른다.
    // 자유 입장(스테이지를 고르지 않은 명령)은 그 지역에서 지금 들어갈 스테이지(고른 스테이지·최전선)의 표를 쓴다(c015 '클리어 수로 1→10 순환' 근사를 대신한다)
    // 일일 던전·탑(L9)은 스테이지가 없다: 선택해 둔 일반 스테이지의 드롭 표를 빌리지 않고 판 계획의 drops를 쓴다(PR #273 Codex P2)
    const daily = this.g.flowController.plan.daily !== undefined;
    this.stageId = daily ? '' : lootStageFor(this.g.flowController.plan, this.g.progress);
    const stage = daily ? undefined : stageById(this.stageId);
    // 드롭률 = 장비 옵션 + 비약(행운)을 합쳐 캡을 건 입장 때 사본(GameSim.buffedTotals, D2c)
    // 난이도(D3b): 하드 판은 아이템 레벨 +10·강화석 ×2·보석 +1(판 계획 diff). 자유 입장·노말은 지도 기본값
    const diff = this.g.flowController.plan.diff === 'normal' ? m.diff : this.g.flowController.plan.diff;
    this.ctx = { region: m.region, diff, table: daily ? this.g.flowController.plan.drops : stage ? stage.drops : 'drop_normal', hero: 'kael', dropRate: this.g.buffedTotals.dropRate };
    this.stoneMult = DIFFICULTY_LOOT[diff].stones;
    this.runIndex++;
    this.rng = seedRng(hashSeed(`${this.seed}:${this.runIndex}:${mapId}`));
    this.gemRng = seedRng(hashSeed(`${this.seed}:${this.runIndex}:${mapId}:gem`));
    this.namedRng = seedRng(hashSeed(`${this.seed}:${this.runIndex}:${mapId}:named`));
    this.gemTier = gemDropTier(m.region, diff);
    this.runNonce = rewardId();
    this.serial = 0;
  }

  clear(): void {
    this.state.active.fill(0);
    this.state.item.fill(null);
    this.ops.fill(null);
    // 이전 판의 장비 참조가 링에 남지 않게 비운다
    this.state.picked.fill(null);
    this.state.pickedHead = 0;
  }

  /** 판 도중 행운의 비약을 마셨다(D2c): 이 판 드롭률을 새 합계로(이벤트 단위, 문맥 객체를 다시 만든다) */
  setDropRate(rate: number): void {
    if (this.ctx) this.ctx = { ...this.ctx, dropRate: rate };
  }

  /**
   * 일반 몹·엘리트 처치(combat.killMob). gradeStep > 0이면 이 처치의 장비 등급 가중을 그만큼 위로 민다(스페셜 챔피언, 난수 소비는 같다)
   */
  onMobKilled(x: number, z: number, elite: boolean, gradeStep = 0): void {
    if (!this.ctx) return;
    rollKillDrops(this.rand, this.ctx, elite ? 'elite' : 'mob', this.stoneMult, this.drops);
    for (let i = 0; i < this.drops.equips; i++) this.spawnGear(x, z, 'wave', gradeStep);
    if (this.drops.stones > 0) this.spawnStones(x, z, this.drops.stones);
    // 보석(D2c): 지역3부터 엘리트 처치마다 GEM_DROP.eliteChance(보석 전용 난수)
    if (elite && this.gemTier > 0 && this.gemRand() < GEM_DROP.eliteChance) this.spawnGem(x, z, false);
  }

  /** 보스 처치(boss.damaged): 이 판 스테이지 표의 확정 드롭(보스 풀). 지역보스 표(10스테이지)만 전설을 굴린다 */
  onBossKilled(x: number, z: number): void {
    if (!this.ctx) return;
    rollKillDrops(this.rand, this.ctx, 'boss', this.stoneMult, this.drops);
    // 보스 처치는 어느 판이든 확정 장비 1개 이상(등급은 그 판 스테이지 표 — 전설은 지역보스 표 판만, c015 조정)
    const n = liveBossDrops(this.drops.bossEquips);
    for (let i = 0; i < n; i++) this.spawnGear(x, z, 'boss');
    if (this.drops.stones > 0) this.spawnStones(x, z, this.drops.stones);
    // 보석(D2c): 중간보스 1개·지역보스 1개(GEM_DROP.boss) 확정(지역3부터)
    if (this.gemTier > 0) {
      const t = this.ctx.table, k = t === 'drop_final' ? GEM_DROP.boss.final : t === 'drop_mid' ? GEM_DROP.boss.mid : 0;
      for (let i = 0; i < k; i++) this.spawnGem(x, z, true);
    }
    // 이름 있는 유니크(BALANCE-1028 3절): 그 지역 그 보스 종류의 유니크마다 source.chance로 1개(드롭률 옵션 영향 없음, 전용 난수)
    const t = this.ctx.table, kind = t === 'drop_final' ? 'final' : t === 'drop_mid' ? 'mid' : null;
    if (kind) for (const u of namedFor(this.ctx.region, kind)) if (this.namedRand() < u.source.chance) this.spawnNamed(x, z, u.id);
  }

  /** 이름 있는 유니크 하나를 바닥에 놓는다(보스 처치 굴림·QA 훅). 던전 판이 아니거나 모르는 id면 아무것도 하지 않는다 */
  spawnNamed(x: number, z: number, id: string): boolean {
    const u = namedUniqueDef(id);
    if (!this.ctx || !u) return false;
    const it = rollNamedItem(this.namedRand, u, this.ctx, `${this.runNonce}-${this.serial++}`);
    if (!it) return false;
    const slot = this.alloc();
    this.ops[slot] = { id: 'pk:' + it.uid, t: 'pickup', item: it };
    const sl = itemSlot(it);
    this.state.item[slot] = it; this.state.aux[slot] = sl ? SLOT_ORDER.indexOf(sl) : -1;
    this.place(slot, x, z, LootKind.Gear, GRADE_INDEX[it.grade], true);
    return true;
  }

  /**
   * 첫 클리어 보상 장부 연산(S3, stageRewards.firstClearReward): 강화석 + 장비(등급 하한·상한은 계획대로).
   * 판 난수와 따로 스테이지 id에서 만든 씨앗·고정 id를 쓴다 — 드롭 수열을 건드리지 않고, 같은 스테이지는 몇 번을 불러도 같은 연산이라
   * 장부의 연산 id·개체 uid 중복 방지로 한 번만 들어간다. 던전 판이 아니면(문맥 없음) 빈 목록.
   */
  firstClearOps(stage: AnyStageId, reward: FirstClearReward): GearOp[] {
    if (!this.ctx) return [];
    const ops: GearOp[] = [];
    if (reward.stones > 0) ops.push({ id: `fc:${stage}:stones`, t: 'stones', tier: reward.stoneTier as 0 | 1 | 2 | 3, n: reward.stones });
    const rng = seedRng(hashSeed(`firstclear:${stage}`));
    const rand = (): number => nextRand(rng);
    // 받는 영웅 기준으로 굴린다(판 드롭 문맥은 카엘 고정 — 라이라는 전용 무기 정의가 아직 없어 방어구·장신구만 나온다, #175 3차 리뷰)
    const ctx: DropContext = { ...this.ctx, dropRate: 0, hero: this.g.heroId };
    firstClearGearPlan(reward).forEach((e, i) => {
      const it = rollGear(rand, ctx, 'boss', `fc-${stage}-${i}`, 0, { min: e.minGrade, max: e.maxGrade });
      ops.push({ id: `fc:${stage}:g${i}`, t: 'pickup', item: it });
    });
    return ops;
  }

  /** 보상 카드 장비 굴림 문맥(content/cardReward): 이 판 지역·난이도·받는 영웅. 던전 판이 아니면 null(장비 카드 없음) */
  cardGearCtx(): DropContext | null {
    return this.ctx ? { ...this.ctx, dropRate: 0, hero: this.g.heroId } : null;
  }

  /** 보석 하나(색 무작위 · 이 판 등급). 연산은 드롭 순간 한 번 만든다(줍기 틱 할당 없음) */
  private spawnGem(x: number, z: number, boss: boolean): void {
    const color = Math.min(GEM_COLORS.length - 1, Math.floor(this.gemRand() * GEM_COLORS.length));
    const slot = this.alloc();
    const code = gemCode(color, this.gemTier);
    this.ops[slot] = { id: `gm:${this.runNonce}-${this.serial++}`, t: 'gems', code, n: 1 };
    this.state.aux[slot] = code; this.state.item[slot] = null;
    this.place(slot, x, z, LootKind.Gem, color, boss, this.gemRand);
  }

  /** 강화석 n개를 바닥에 놓는다(스페셜 몬스터 보상). 드롭 문맥이 없으면(마을) 아무것도 하지 않는다 */
  dropStones(x: number, z: number, n: number): void {
    if (this.ctx && n > 0) this.spawnStones(x, z, n);
  }

  private spawnGear(x: number, z: number, pool: 'wave' | 'boss', gradeStep = 0): void {
    const uid = `${this.runNonce}-${this.serial++}`;
    const it = rollGear(this.rand, this.ctx!, pool, uid, gradeStep);
    const slot = this.alloc();
    this.ops[slot] = { id: 'pk:' + it.uid, t: 'pickup', item: it };
    const sl = itemSlot(it);
    this.state.item[slot] = it; this.state.aux[slot] = sl ? SLOT_ORDER.indexOf(sl) : -1;
    this.place(slot, x, z, LootKind.Gear, GRADE_INDEX[it.grade], pool === 'boss');
  }

  private spawnStones(x: number, z: number, n: number): void {
    const slot = this.alloc();
    const tier = stoneTierForIlvl(this.ctxIlvl()) as 0 | 1 | 2 | 3;
    this.ops[slot] = { id: `st:${this.runNonce}-${this.serial++}`, t: 'stones', tier, n };
    this.state.aux[slot] = n; this.state.tier[slot] = tier; this.state.item[slot] = null;
    this.place(slot, x, z, LootKind.Stone, 0, false);
  }

  /** 지금 문맥의 아이템 레벨(지역 장비 레벨 = 권장 구간 시작 + 난이도 가산, economy 정의) — 강화석 등급 */
  private ctxIlvl(): number {
    const c = this.ctx!;
    return REGIONS_90D[c.region - 1].levels[0] + DIFFICULTY_ILVL_ADD[c.diff];
  }

  private place(slot: number, x: number, z: number, kind: number, grade: number, boss: boolean, rand: () => number = this.rand): void {
    const s = this.state;
    // 몹 자리에서 조금 흩어진다(걸을 수 있는 자리만, 아니면 제자리). 보석은 보석 전용 난수로 흩는다(장비 수열 보존)
    const a = rand() * Math.PI * 2, r = LOOT_SCATTER * (0.35 + 0.65 * rand());
    const nx = x + Math.sin(a) * r, nz = z + Math.cos(a) * r;
    const ok = this.g.walkable(nx, nz, 0.2);
    s.x[slot] = ok ? nx : x; s.z[slot] = ok ? nz : z;
    s.kind[slot] = kind; s.grade[slot] = grade; s.age[slot] = 0; s.active[slot] = 1;
    this.order[slot] = ++this.orderSeq;
    s.drops++;
    const e = this.g.emit(Ev.LootDrop);
    e.x = s.x[slot]; e.z = s.z[slot]; e.a = kind; e.b = grade; e.c = slot; e.d = boss ? 1 : 0;
    // 이름 있는 유니크는 놓인 개체(item.nu)로 판정한다 — 렌더 주황 기둥(render/loot.ts)과 같은 기준이라 어느 경로로 나와도 연출이 어긋나지 않는다(PR #261 리뷰 P2)
    e.flags = kind === LootKind.Gear && s.item[slot]?.nu ? 1 : 0;
  }

  /** 빈 슬롯. 가득 차면 가장 오래된 드롭을 바로 가방에 넣고 그 슬롯을 쓴다 */
  private alloc(): number {
    const s = this.state;
    let oldest = 0;
    for (let i = 0; i < s.capacity; i++) {
      if (!s.active[i]) return i;
      if (this.order[i] < this.order[oldest]) oldest = i;
    }
    this.collect(oldest, PickupBy.Overflow);
    return oldest;
  }

  /** 고정 스텝(던전): 나이 증가, 영웅 반경·펫 반경 안 드롭 줍기 */
  step(): void {
    const s = this.state, h = this.g.hero;
    if (!combatScene(this.g.flow.scene) || h.hp <= 0) return;
    const hr2 = HERO_PICKUP_RADIUS * HERO_PICKUP_RADIUS, pr2 = s.petRadius * s.petRadius;
    for (let i = 0; i < s.capacity; i++) {
      if (!s.active[i]) continue;
      s.age[i]++;
      if (s.age[i] < LOOT_ARM_TICKS) continue;
      const dx = s.x[i] - h.x, dz = s.z[i] - h.z, d2 = dx * dx + dz * dz;
      if (d2 <= hr2) this.collect(i, PickupBy.Hero);
      else if (pr2 > 0 && d2 <= pr2) this.collect(i, PickupBy.Pet);
    }
  }

  /** 판 클리어: 바닥에 남은 드롭을 모두 거둔다. force = 귀환서처럼 클리어 설정과 무관하게 항상 거둔다 */
  sweep(force = false): void {
    if (!force && !SWEEP_ON_CLEAR) return;
    const s = this.state;
    for (let i = 0; i < s.capacity; i++) if (s.active[i]) this.collect(i, PickupBy.Sweep);
  }

  /** 드롭 하나를 장부에 넣는다(연산 id = 개체 uid → 재시도·재로드에도 한 번만) */
  private collect(i: number, by: number): void {
    const s = this.state, g = this.g;
    if (!s.active[i]) return;
    s.active[i] = 0;
    let salvaged = false, detail = 0, ring = -1, tierBits = 0, namedBit = 0; // ring = 고리 칸(-1 없음), 이벤트 y는 칸 + 1(0 = 없음)
    const op = this.ops[i];
    this.ops[i] = null;
    s.item[i] = null;
    if (!op) return;
    if (op.t === 'pickup') {
      // 줍기 알림 띠가 이름·그림·비교를 읽도록 개체를 고리에 둔다(이벤트 y = 칸 + 1, 새 객체 없음)
      ring = s.pickedHead; s.pickedHead = (ring + 1) % PICKED_RING; s.picked[ring] = op.item;
      salvaged = g.gear.submit(op).code === 'auto_salvaged';
      const slot = itemSlot(op.item);
      detail = slot ? SLOT_ORDER.indexOf(slot) : -1;
      if (op.item.nu) namedBit = 2;
    } else if (op.t === 'stones') {
      detail = op.n; tierBits = op.tier << 1;
      g.gear.submit(op);
    } else if (op.t === 'gems') {
      // 보석: d = 보석 코드(줍기 띠 문구)
      detail = op.code;
      g.gear.submit(op);
    }
    s.pickups++;
    if (by === PickupBy.Pet) s.petPickups++;
    const e = g.emit(Ev.LootPickup);
    e.x = s.x[i]; e.y = ring + 1; e.z = s.z[i]; e.a = s.kind[i]; e.b = s.grade[i]; e.c = by; e.d = detail;
    e.flags = (salvaged ? 1 : 0) | tierBits | namedBit;
  }
}
